// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
// Impression d'un fragment de page hors de l'origine du tableau de bord.
//
// Une fenêtre window.open sur about:blank hériterait de cette origine, donc du
// jeton d'administration rangé dans localStorage. D'où, comme pour l'aperçu des
// contrats (apps/admin/public/contrats.html) :
//   - un <iframe> éphémère, sandbox sans allow-scripts ; allow-same-origin
//     permet d'y appeler print(), allow-modals d'ouvrir le dialogue ;
//   - une CSP en tête qui interdit tout script et toute ressource externe ;
//   - un titre échappé et une feuille de style qui ne peut pas fermer sa balise.
//
// Sans dépendance (ni React, ni MUI) : un test du backend joue
// printableDocument et escapeHtml sous node.

// Les styles en ligne (ceux de React) et les images en data: restent permis :
// le QR de l'étiquette est un <svg> en ligne, que la CSP ne touche pas.
export const PRINT_CSP = "default-src 'none'; style-src 'unsafe-inline'; img-src data:; "
                            + "base-uri 'none'; form-action 'none'";

const ENTITIES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

export function escapeHtml(valeur) {
  if (valeur === null || valeur === undefined) return '';
  return String(valeur).replace(/[&<>"']/g, (c) => ENTITIES[c]);
}

/** Document HTML complet, CSP en tête. `body` est du HTML déjà sûr (le DOM
 *  rendu par React, dont le texte est échappé) ; `titre` et `style` sont du
 *  texte, rendus inoffensifs ici. */
export function printableDocument({ titre = '', style = '', corps: body = '' } = {}) {
  // « < » n'a pas d'usage légitime dans ces feuilles de style : son
  // échappement CSS (\3c) empêche qu'une valeur ferme la balise <style>.
  const feuille = String(style || '').replace(/</g, '\\3c ');
  return '<!DOCTYPE html><meta http-equiv="Content-Security-Policy" content="'
    + PRINT_CSP + '">'
    + '<html lang="fr"><head><meta charset="utf-8">'
    + `<title>${escapeHtml(titre)}</title>`
    + (feuille ? `<style>${feuille}</style>` : '')
    + `</head><body>${String(body || '')}</body></html>`;
}

/** Imprime `html` depuis un cadre éphémère sandbox, sans script. `onError`
 *  est appelé si le navigateur refuse l'impression depuis le cadre. */
export function printInFrame(html, onError) {
  const frame = document.createElement('iframe');
  frame.setAttribute('sandbox', 'allow-same-origin allow-modals');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  Object.assign(frame.style, {
    position: 'fixed', right: '0', bottom: '0', width: '0', height: '0',
    border: '0', visibility: 'hidden',
  });
  frame.onload = () => {
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch (e) {
      if (onError) onError(e);
    }
    // Le dialogue d'impression est modal : le cadre peut partir ensuite.
    setTimeout(() => frame.remove(), 1000);
  };
  frame.srcdoc = html;
  document.body.appendChild(frame);
}
